import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";
import { formAutocompletePaperSx } from "styles/formStyleComponents";
import {
  sideDrawerPaperSx,
} from "common/Components/SideDrawer/SideDrawer.constants";

const uiFont = appThemeFonts.family.ui;

const focusRing = {
  outline: "2px solid #0B5FA5",
  outlineOffset: "2px",
};

export const customerDetailsDrawerPaperSx = {
  ...sideDrawerPaperSx,
  width: "min(740px, 100vw)",
};

export const DrawerSubtitle = styled(Typography)({
  fontFamily: uiFont,
  fontSize: "12.5px",
  fontWeight: 400,
  color: appThemeColors.text.muted,
  margin: 0,
  lineHeight: 1.4,
});

export const DrawerTitleStatusWrap = styled("span")({
  marginLeft: 6,
  verticalAlign: "middle",
});

export const DrawerSplitLayout = styled(Box)({
  display: "flex",
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  borderTop: `1px solid ${appThemeColors.border.default}`,
});

export const DrawerTabsPane = styled(Box)({
  width: 190,
  flexShrink: 0,
  padding: "8px 0",
  borderRight: `1px solid ${appThemeColors.border.default}`,
  backgroundColor: appThemeColors.background.fill,
});

export const DrawerTabPanel = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  minHeight: 0,
});

export const drawerTabsSx = {
  minHeight: "auto",
  alignItems: "stretch",
  "& .MuiTabs-indicator": {
    left: 0,
    width: 3,
    borderRadius: 0,
    backgroundColor: appThemeColors.blue.dark,
  },
};

export const drawerTabSx = {
  alignItems: "flex-start",
  justifyContent: "flex-start",
  textAlign: "left",
  textTransform: "none",
  fontFamily: uiFont,
  fontWeight: 500,
  fontSize: "13.5px",
  color: appThemeColors.text.muted,
  px: 2,
  py: 1.25,
  minHeight: 44,
  "&.Mui-selected": {
    color: appThemeColors.text.heading,
    fontWeight: 600,
  },
};

export const drawerFooterSx = {
  backgroundColor: appThemeColors.background.paper2,
};

export const ConnectionsStack = styled(Box)({
  display: "flex",
  flexDirection: "column",
  padding: 20,
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
});

export const ConnectionSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

export const ConnectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
});

export const ConnectionTitle = styled(Typography)({
  fontFamily: uiFont,
  fontSize: "13.5px",
  fontWeight: 600,
  color: appThemeColors.text.heading,
  lineHeight: 1.3,
});
export const ConnectionStatus = styled(Typography)({
  fontFamily: uiFont,
  fontSize: "11px",
  color: appThemeColors.text.muted,
  lineHeight: 1.3,
});

export const ConnectionTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "$error",
})(({ $error }) => ({
  fontFamily: uiFont,
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    borderRadius: 9,
    backgroundColor: appThemeColors.background.paper,
    fontSize: "13.5px",
    fontFamily: uiFont,
    transition: "outline 120ms ease, outline-offset 120ms ease",
    "& fieldset": {
      borderColor: $error
        ? appThemeColors.semantic.danger
        : appThemeColors.border.strong,
      borderWidth: 1,
    },
    "&:hover fieldset": {
      borderColor: $error
        ? appThemeColors.semantic.danger
        : appThemeColors.text.lightBlue,
    },
    "&.Mui-focused": {
      ...focusRing,
      "& fieldset": {
        borderColor: $error
          ? appThemeColors.semantic.danger
          : appThemeColors.border.strong,
        borderWidth: "1px !important",
      },
    },
    "&.Mui-disabled": {
      backgroundColor: appThemeColors.background.fill,
      "& fieldset": {
        borderColor: appThemeColors.border.default,
      },
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "0 12px",
    height: 38,
    boxSizing: "border-box",
    fontSize: "13.5px",
    fontFamily: uiFont,
    color: appThemeColors.text.primary,
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: appThemeColors.text.faint,
    opacity: 1,
  },
  "& .MuiFormHelperText-root": {
    fontFamily: uiFont,
    fontSize: "11.5px",
    marginLeft: 0,
    marginTop: 6,
    color: $error ? appThemeColors.semantic.danger : appThemeColors.text.faint,
  },
}));

export const facilitiesAutocompleteSx = {
  width: "100%",
  flex: 1,
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    borderRadius: "9px",
    backgroundColor: appThemeColors.background.paper,
    fontFamily: uiFont,
    fontSize: "13.5px",
    flexWrap: "wrap",
    alignItems: "flex-start",
    py: 0.75,
    "& fieldset": {
      borderColor: appThemeColors.border.strong,
      borderWidth: 1,
    },
    "&:hover fieldset": {
      borderColor: appThemeColors.text.lightBlue,
    },
    "&.Mui-focused": {
      "& fieldset": {
        borderColor: appThemeColors.text.lightBlue,
        borderWidth: "2px !important",
      },
    },
    "&.Mui-disabled": {
      backgroundColor: appThemeColors.background.fill,
      "& fieldset": {
        borderColor: appThemeColors.border.default,
      },
    },
  },
  "& .MuiAutocomplete-input": {
    minWidth: "48px !important",
    fontFamily: uiFont,
    fontSize: "13.5px",
  },
  "& .MuiChip-root": {
    fontFamily: uiFont,
    fontSize: "12px",
    height: 24,
    borderColor: appThemeColors.border.default,
    backgroundColor: appThemeColors.background.fill,
    color: appThemeColors.text.primary,
  },
};

const facilitiesOptionTextSx = {
  fontFamily: uiFont,
  fontSize: "13.5px",
};

export const facilitiesListboxStyle = {
  maxHeight: "min(40vh, 260px)",
  overflow: "auto",
  ...facilitiesOptionTextSx,
};

export const getFacilitiesSlotProps = (modalZIndex) => ({
  popper: {
    placement: "bottom-start",
    strategy: "fixed",
    sx: { zIndex: modalZIndex },
  },
  paper: {
    sx: {
      mt: 0.5,
      ...formAutocompletePaperSx,
      "& .MuiAutocomplete-option": facilitiesOptionTextSx,
      "& .MuiAutocomplete-option .MuiListItemText-primary":
        facilitiesOptionTextSx,
      "& .MuiAutocomplete-option[aria-selected='true'] .MuiSvgIcon-root": {
        color: appThemeColors.text.lightBlue,
      },
    },
  },
});

export const facilitiesTagsWrapSx = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 0.5,
  width: "100%",
  alignItems: "center",
  "& .MuiChip-root": {
    flexShrink: 0,
  },
};

export const EntitlementsRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
});

export const EntitlementsHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 20px",
  borderBottom: `1px solid ${appThemeColors.background.soft}`,
  flexShrink: 0,
});

export const EntitlementsHeaderLabel = styled(Typography)({
  fontFamily: uiFont,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: appThemeColors.text.faint,
  lineHeight: 1.3,
});

export const EntitlementsAccessCell = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 4,
});

export const EntitlementsScroll = styled(Box)({
  flex: 1,
  overflowY: "auto",
});

export const EntitlementRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  minHeight: 52,
  padding: "0 20px",
  borderBottom: `1px solid ${appThemeColors.background.soft}`,
});

export const EntitlementLabel = styled(Typography)({
  fontFamily: uiFont,
  fontSize: "13.5px",
  fontWeight: 500,
  color: appThemeColors.text.primary,
  flex: 1,
  minWidth: 0,
});

export const EntitlementsLoadingBox = styled(Box)({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 32,
});

export const entitlementsInfoButtonSx = {
  p: 0.25,
  color: appThemeColors.text.muted,
  "&:hover": {
    color: appThemeColors.text.heading,
    backgroundColor: "transparent",
  },
};

export const entitlementsTooltipSlotProps = {
  tooltip: {
    sx: {
      // bgcolor: appThemeColors.text.heading,
      color: appThemeColors.neutral.white,
      fontFamily: uiFont,
      fontSize: "12.5px",
      fontWeight: 400,
      lineHeight: 1.4,
      maxWidth: 280,
      px: 1.25,
      py: 0.75,
      "& .MuiTooltip-arrow": {
        color: appThemeColors.text.heading,
      },
    },
  },
};
